import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { bookingsAPI } from '../api/bookings';
import { providersAPI } from '../api/providers';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { CalendarIcon, ClockIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid, CheckBadgeIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../utils/formatter';
import toast from 'react-hot-toast';

const MyReviews = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // unified list: { booking, provider, serviceName, review? }
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(() => searchParams.get('filter') || 'All');
  const [providerProfiles, setProviderProfiles] = useState({});

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingItem, setReviewingItem] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewErrors, setReviewErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch completed bookings and user's reviews in parallel
      const [bookingsData, myReviewsData] = await Promise.all([
        bookingsAPI.getBookings({ clientId: user.id }, { silent: true }),
        providersAPI.getMyReviews().catch(err => {
          console.error('Failed to fetch my reviews:', err?.response?.status, err?.response?.data);
          return { reviews: [] };
        }),
      ]);

      const allBookings = Array.isArray(bookingsData) ? bookingsData : bookingsData.bookings || [];
      const completed = allBookings.filter(b => b.status === 'COMPLETED' || b.status === 'PAID');
      const normalizeReview = (review) => ({
        ...review,
        comment: review.comment || review.content || review.text || review.message || '',
      });
      const myReviews = (myReviewsData?.reviews || []).map(normalizeReview);

      const reviewsByProvider = {};
      myReviews.forEach((review) => {
        const existing = reviewsByProvider[review.providerId];
        if (!existing || new Date(review.createdAt || 0) > new Date(existing.createdAt || 0)) {
          reviewsByProvider[review.providerId] = review;
        }
      });

      // Get all provider IDs from bookings
      const uniqueProviderIds = [...new Set(completed.map(b => b.providerId).filter(Boolean))];

      // Fetch provider profiles and service names for completed bookings.
      const profiles = {};
      const svcMap = {};
      await Promise.all(uniqueProviderIds.map(async (pid) => {
        try {
          // Use provider from review include if available
          const reviewProvider = reviewsByProvider[pid]?.provider;
          if (reviewProvider) {
            profiles[pid] = reviewProvider;
          } else {
            profiles[pid] = await providersAPI.getProvider(pid);
          }
          const svcData = await providersAPI.getProviderServices(pid);
          const servicesList = svcData?.services || (Array.isArray(svcData) ? svcData : []);
          servicesList.forEach(s => { svcMap[s.id] = s.name; });
        } catch {}
      }));

      // Also add profiles for reviews from providers not in bookings
      for (const review of myReviews) {
        if (!profiles[review.providerId] && review.provider) {
          profiles[review.providerId] = review.provider;
        }
      }

      setProviderProfiles(profiles);

      // Build unified items list
      const unified = [];
      const seenProviders = new Set();

      // 1) Reviewed providers (from reviewsByProvider map)
      const allReviewedPids = Object.keys(reviewsByProvider);
      for (const pid of allReviewedPids) {
        seenProviders.add(pid);
        const review = reviewsByProvider[pid];
        const relatedBooking = completed.find(b => b.providerId === pid);
        unified.push({
          booking: relatedBooking || { id: review.id, providerId: pid, startAt: review.createdAt },
          provider: profiles[pid],
          serviceName: relatedBooking ? (svcMap[relatedBooking.serviceId] || 'Service') : 'Service',
          review,
        });
      }

      // 2) Pending (completed bookings for providers not yet reviewed)
      const sorted = [...completed].sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
      for (const booking of sorted) {
        if (seenProviders.has(booking.providerId)) continue;
        seenProviders.add(booking.providerId);
        unified.push({
          booking,
          provider: profiles[booking.providerId],
          serviceName: svcMap[booking.serviceId] || booking.service?.name || 'Service',
          review: null,
        });
      }

      setItems(unified);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = (item) => {
    setReviewingItem(item);
    setRating(0);
    setHoverRating(0);
    setComment('');
    setReviewErrors({});
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    const nextErrors = {};
    if (rating === 0) nextErrors.rating = 'Please select a rating';
    if (!comment.trim()) nextErrors.comment = 'Please write a comment';
    setReviewErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (!reviewingItem) return;

    setSubmitting(true);
    try {
      const result = await providersAPI.submitReview(reviewingItem.booking.providerId, { rating, comment });
      toast.success('Review submitted! Thank you.');

      // Update the item in state with the new review
      setItems(prev => prev.map(item =>
        item.booking.id === reviewingItem.booking.id
          ? { ...item, review: result || { rating, comment, createdAt: new Date().toISOString() } }
          : item
      ));

      setShowReviewModal(false);
      setReviewingItem(null);
      setRating(0);
      setComment('');
    } catch (error) {
      const errMsg = error?.response?.data?.error || '';
      if (errMsg.toLowerCase().includes('already reviewed') || error?.response?.status === 409) {
        toast.success('You have already reviewed this provider.');
        // Mark as reviewed in state
        setItems(prev => prev.map(item =>
          item.booking.id === reviewingItem.booking.id
            ? { ...item, review: { rating, comment, createdAt: new Date().toISOString() } }
            : item
        ));
        setShowReviewModal(false);
        setReviewingItem(null);
        setRating(0);
        setComment('');
      } else {
        toast.error('Failed to submit review');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Filter
  const pending = items.filter(i => !i.review);
  const reviewed = items.filter(i => i.review);

  let displayItems = items;
  if (activeFilter === 'Pending') displayItems = pending;
  else if (activeFilter === 'Reviewed') displayItems = reviewed;

  // Search
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    displayItems = displayItems.filter(item => {
      const pName = item.provider?.businessName || item.provider?.name || '';
      return item.serviceName.toLowerCase().includes(q) || pName.toLowerCase().includes(q) || (item.review?.comment || '').toLowerCase().includes(q);
    });
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px' }}>

        {/* Top bar — search + filters + action */}
        <div className="flex items-center gap-3 mb-0" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--gray-4)' }}>
          {/* Search */}
          <div className="relative flex-1">
            <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: 'var(--gray-9)' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: '8px', border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)', color: 'var(--gray-12)', fontSize: '14px', outline: 'none' }}
            />
          </div>

          {/* Filters */}
          <div className="flex gap-1">
            {[
              { key: 'All', label: 'All', icon: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg> },
              { key: 'Pending', label: 'Pending', icon: <StarIcon style={{ width: '16px', height: '16px' }} /> },
              { key: 'Reviewed', label: 'Reviewed', icon: <StarSolid style={{ width: '16px', height: '16px' }} /> },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                title={label}
                style={{
                  width: '34px', height: '34px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: activeFilter === key ? 'var(--orange-9)' : 'transparent',
                  color: activeFilter === key ? '#fff' : 'var(--gray-9)',
                }}
                onMouseEnter={e => { if (activeFilter !== key) { e.currentTarget.style.backgroundColor = 'var(--gray-3)'; } }}
                onMouseLeave={e => { if (activeFilter !== key) { e.currentTarget.style.backgroundColor = 'transparent'; } }}
              >
                {icon}
              </button>
            ))}
          </div>

          {/* Action */}
          <Button onClick={() => navigate('/search')} size="small">
            <CalendarIcon style={{ width: '14px', height: '14px' }} />
            Book Service
          </Button>
        </div>

        {/* List */}
        <div className="mt-4">
          {loading ? (
            <div className="flex justify-center py-16"><Spinner size="large" /></div>
          ) : displayItems.length === 0 ? (
            <div className="text-center py-12">
              <StarIcon style={{ width: '40px', height: '40px', color: 'var(--gray-7)', margin: '0 auto 8px' }} />
              <p style={{ color: 'var(--gray-10)', fontSize: '14px' }}>
                {searchQuery || activeFilter !== 'All' ? 'No reviews match your filter' : 'No reviews yet'}
              </p>
            </div>
          ) : (
            <div>
              {displayItems.map((item) => {
                const { booking, provider, serviceName, review } = item;
                const date = new Date(booking.startAt);
                const dateStr = date.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const providerName = provider?.businessName || provider?.name || 'Provider';
                const providerPhoto = provider?.photoUrl;
                const isVerified = provider?.isApproved;
                const isReviewed = !!review;

                return (
                  <div key={booking.id} style={{ borderBottom: '1px solid var(--gray-4)', padding: '16px 0' }}>
                    <div className="flex items-start gap-3">
                      {/* Provider profile pic */}
                      {providerPhoto ? (
                        <img src={providerPhoto} alt={providerName}
                          style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: '2px' }} />
                      ) : (
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                          backgroundColor: 'var(--orange-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)',
                        }}>
                          {providerName.charAt(0).toUpperCase()}
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Row 1: Provider name + right side */}
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="flex items-center gap-1" style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>
                              {providerName}
                              {isVerified && <CheckBadgeIcon style={{ width: '16px', height: '16px', color: 'var(--orange-9)' }} />}
                            </h4>
                            <span style={{ fontSize: '11px', color: 'var(--gray-9)' }}>@{providerName.toLowerCase().replace(/\s+/g, '')}</span>
                          </div>
                          <div className="flex items-center gap-3" style={{ flexShrink: 0 }}>
                            {isReviewed ? (
                              <>
                                <span style={{
                                  fontSize: '11px', fontWeight: 600, color: '#22c55e', textTransform: 'uppercase',
                                  backgroundColor: '#22c55e15', padding: '3px 10px', borderRadius: '20px',
                                }}>
                                  Reviewed
                                </span>
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map(s => (
                                    s <= review.rating
                                      ? <StarSolid key={s} style={{ width: '14px', height: '14px', color: '#facc15' }} />
                                      : <StarIcon key={s} style={{ width: '14px', height: '14px', color: 'var(--gray-5)' }} />
                                  ))}
                                </div>
                              </>
                            ) : (
                              <>
                                <span style={{
                                  fontSize: '11px', fontWeight: 600, color: '#f97316', textTransform: 'uppercase',
                                  backgroundColor: '#f9731615', padding: '3px 10px', borderRadius: '20px',
                                }}>
                                  Pending
                                </span>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)', fontFamily: "'Poppins', sans-serif" }}>
                                  {formatCurrency(booking.totalPaid || booking.subtotal || 0)}
                                </span>
                                <button onClick={() => handleOpenReview(item)} className="flex items-center gap-1.5"
                                  style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--orange-6)', backgroundColor: 'transparent', color: 'var(--orange-9)', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>
                                  <StarIcon style={{ width: '13px', height: '13px' }} />
                                  Write Review
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Row 2: Service name */}
                        <p style={{ fontSize: '13px', color: 'var(--gray-12)', margin: '4px 0 0', fontWeight: 500 }}>
                          {serviceName}
                        </p>

                        {/* Row 3: Review comment (if reviewed) */}
                        {isReviewed && review.comment && (
                          <p className="flex items-start gap-1.5" style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '4px 0 0', lineHeight: 1.5 }}>
                            <svg style={{ width: '13px', height: '13px', flexShrink: 0, marginTop: '2px' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                            <span>"{review.comment}"</span>
                          </p>
                        )}

                        {/* Row 4: Date + time */}
                        <div className="flex items-center gap-4" style={{ marginTop: '6px' }}>
                          <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: 'var(--gray-9)' }}>
                            <CalendarIcon style={{ width: '13px', height: '13px' }} />
                            {dateStr}
                          </span>
                          <span className="flex items-center gap-1.5" style={{ fontSize: '12px', color: 'var(--gray-9)' }}>
                            <ClockIcon style={{ width: '13px', height: '13px' }} />
                            {time}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Showing count */}
          {displayItems.length > 0 && (
            <p style={{ fontSize: '13px', color: 'var(--gray-9)', marginTop: '16px', textAlign: 'left' }}>
              Showing {displayItems.length} of {items.length} reviews
            </p>
          )}
        </div>
      </div>

      {/* Review Modal */}
      <Modal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        title="Write a Review"
      >
        {reviewingItem && (
          <div>
            <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 14px' }}>
              Rate your experience with {reviewingItem.serviceName}
            </p>

            {/* Star rating */}
            <div className="flex items-center gap-1" style={{ marginBottom: '14px' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => {
                    setRating(star);
                    setReviewErrors(prev => ({ ...prev, rating: '' }));
                  }}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                >
                  {star <= (hoverRating || rating)
                    ? <StarSolid style={{ width: '26px', height: '26px', color: '#facc15' }} />
                    : <StarIcon style={{ width: '26px', height: '26px', color: 'var(--gray-5)' }} />
                  }
                </button>
              ))}
              {rating > 0 && (
                <span style={{ fontSize: '12px', color: 'var(--gray-10)', marginLeft: '8px' }}>
                  {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                </span>
              )}
            </div>
            {reviewErrors.rating && (
              <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', margin: '-8px 0 12px', textAlign: 'left' }}>
                {reviewErrors.rating}
              </p>
            )}

            <textarea
              value={comment}
              onChange={e => {
                setComment(e.target.value);
                setReviewErrors(prev => ({ ...prev, comment: '' }));
              }}
              placeholder="Share your experience..."
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: `1px solid ${reviewErrors.comment ? 'var(--red-9, #e5484d)' : 'var(--gray-5)'}`, backgroundColor: 'var(--color-background)',
                color: 'var(--gray-12)', fontSize: '13px', resize: 'vertical',
                outline: 'none', fontFamily: 'inherit',
              }}
            />
            {reviewErrors.comment && (
              <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', margin: '4px 0 0', textAlign: 'left' }}>
                {reviewErrors.comment}
              </p>
            )}

            <div className="flex gap-2 justify-end" style={{ marginTop: '14px' }}>
              <button
                onClick={() => setShowReviewModal(false)}
                style={{
                  padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                  border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                  color: 'var(--gray-11)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReview}
                disabled={submitting}
                style={{
                  padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                  border: 'none',
                  backgroundColor: 'var(--orange-9)',
                  color: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyReviews;
